import React from "react";

interface PDAContentSectionProps {
  title?: string;
  children: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
}

interface PDAContentProps {
  children: React.ReactNode;
}

interface PDAParagraphProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

interface PDAListProps {
  children: React.ReactNode;
  className?: string;
}

interface PDALiProps {
  children: React.ReactNode;
  index?: number | string;
  className?: string;
  numberingType?: "auto-index" | "manual-number" | "none";
}

export const PDAContentSection: React.FC<PDAContentSectionProps> = ({
  title,
  children,
  className = "mb-[20px]",
  icon,
}) => {
  // Check if title contains a number and adjust font size accordingly
  const hasNumber = title && /\d/.test(title);
  const fontSize = hasNumber ? "24px" : "30px";

  return (
    <div style={{ marginBottom: "30px" }} className={className}>
      {title && (
        <h2
          className="font-bold text-site-blue-dark mb-[10px] flex items-center gap-2"
          style={{ fontSize }}
        >
          {icon}
          {title}
        </h2>
      )}
      {children}
    </div>
  );
};

export const PDAContent: React.FC<PDAContentProps> = ({ children }) => {
  return <>{children}</>;
};

export const PDAParagraph: React.FC<PDAParagraphProps> = ({
  children,
  className = "mb-4",
  style,
}) => {
  return (
    <p className={className} style={{ fontSize: "20px", ...style }}>
      {children}
    </p>
  );
};

const childHasExplicitIndex = (child: React.ReactNode): boolean => {
  if (!React.isValidElement(child)) return false;

  const props = child.props as Partial<PDALiProps>;
  return props.index !== undefined && props.index !== null;
};

const childStartsWithNumber = (child: React.ReactNode): boolean => {
  if (typeof child === "string") {
    return /^\d+\.\s*/.test(child.trim());
  }

  if (React.isValidElement(child)) {
    const props = child.props as { children?: React.ReactNode };
    if (typeof props.children === "string") {
      return /^\d+\.\s*/.test(props.children.trim());
    }
  }

  return false;
};

const getNumberingType = (
  child: React.ReactNode
): "auto-index" | "manual-number" | "none" => {
  if (!React.isValidElement(child)) return "none";

  const props = child.props as Partial<PDALiProps>;

  // Check if it has an explicit index prop (auto-generated from component)
  if (props.index !== undefined && props.index !== null) {
    return "auto-index";
  }

  // Check if it starts with manual numbering like "1.", "2.", etc.
  const children = props.children as React.ReactNode;
  if (typeof children === "string" && /^\d+\.\s*/.test(children.trim())) {
    return "manual-number";
  }

  return "none";
};

export const PDAList: React.FC<PDAListProps> = ({
  children,
  className = "",
}) => {
  const items = React.Children.toArray(children);

  const hasIndexedItems = items.some(childHasExplicitIndex);
  const hasNumberedTextItems = items.some(childStartsWithNumber);

  const useCustomNumberStyle = hasIndexedItems || hasNumberedTextItems;

  const listClass = useCustomNumberStyle
    ? `ml-0 list-none space-y-2 ${className}`.trim()
    : `ml-6 list-disc space-y-2 ${className}`.trim();

  return (
    <ul className={listClass} style={{ fontSize: "20px" }}>
      {items.map((child, index) => {
        if (!React.isValidElement(child)) return child;

        const numberingType = getNumberingType(child);
        const childProps = child.props as Partial<PDALiProps>;

        // Clone the child with additional numbering type information
        // Preserve the original key if it exists, otherwise use the index
        const originalKey = (child as any).key;
        return React.cloneElement(child, {
          ...childProps,
          numberingType,
          key: originalKey !== null ? originalKey : index,
        } as any);
      })}
    </ul>
  );
};

export const PDALi: React.FC<PDALiProps> = ({
  children,
  index,
  className = "",
  numberingType = "none",
}) => {
  const hasIndex = index !== undefined && index !== null;

  // Handle auto-index numbering (from component index prop)
  if (hasIndex && numberingType === "auto-index") {
    return (
      <li className={`flex items-start gap-2 ${className}`.trim()}>
        <span className="shrink-0 font-semibold text-[18px] text-site-blue-dark flex items-start justify-center mt-[2px] min-w-[20px]">
          {index}.
        </span>
        <span
          style={{ fontSize: "20px", fontFamily: "Work Sans" }}
          className="flex-1"
        >
          {children}
        </span>
      </li>
    );
  }

  // Handle manual numbering (text starting with "1.", "2.", etc.)
  if (numberingType === "manual-number") {
    return (
      <li className={`flex items-start gap-2 ${className}`.trim()}>
        <span className="shrink-0 font-bold -mt-[2.5px] text-[20px] text-site-green-dark min-w-[24px]">
          {typeof children === "string" ? children.match(/^\d+\./)?.[0] : ""}
        </span>
        <span
          style={{ fontSize: "20px", fontFamily: "Work Sans" }}
          className="flex-1"
        >
          {typeof children === "string"
            ? children.replace(/^\d+\.\s*/, "")
            : children}
        </span>
      </li>
    );
  }

  // Handle auto-index without explicit numbering type (backward compatibility)
  if (hasIndex) {
    return (
      <li className={`flex items-start gap-2 ${className}`.trim()}>
        <span className="shrink-0 font-semibold text-[18px] text-site-blue-dark flex items-start justify-center mt-[2px] min-w-[20px]">
          {index}.
        </span>
        <span
          style={{ fontSize: "20px", fontFamily: "Work Sans" }}
          className="flex-1"
        >
          {children}
        </span>
      </li>
    );
  }

  // Default list item styling
  return <li className={className}>{children}</li>;
};

export const PDABold: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <strong>{children}</strong>;
};

export const PDABreak: React.FC = () => {
  return <br />;
};
